import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LazyImage } from './lazy-image';
import { getPublishedPosts, estimateReadingTime } from 'utils/blogHelpers';

const FALLBACK_IMAGE =
	'https://cdn.21st.dev/assets/mirror/cc/ccf6e78beb7f7cc5b720069cb4f86696912be109614973d49df165d1e51631c8.svg';

const formatDate = (value) => {
	if (!value) return '';
	// Firestore Timestamps have a toDate(); plain Date/string also handled.
	const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export function BlogSection() {
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let isMounted = true;
		(async () => {
			const { posts: fetched } = await getPublishedPosts();
			if (isMounted) {
				setPosts(fetched);
				setLoading(false);
			}
		})();
		return () => {
			isMounted = false;
		};
	}, []);

	return (
		<div className="mx-auto w-full max-w-5xl grow">
			<div
				aria-hidden
				className="absolute inset-0 isolate contain-strict -z-10 opacity-60"
			>
				<div className="-rotate-45 bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,--theme(--color-foreground/.06)_0,hsla(0,0%,55%,.02)_50%,--theme(--color-foreground/.01)_80%)] absolute top-0 left-0 h-320 w-140 -translate-y-87.5 rounded-full" />
				<div className="-rotate-45 bg-[radial-gradient(50%_50%_at_50%_50%,--theme(--color-foreground/.04)_0,--theme(--color-foreground/.01)_80%,transparent_100%)] absolute top-0 left-0 h-320 w-60 [translate:5%_-50%] rounded-full" />
				<div className="-rotate-45 bg-[radial-gradient(50%_50%_at_50%_50%,--theme(--color-foreground/.04)_0,--theme(--color-foreground/.01)_80%,transparent_100%)] absolute top-0 left-0 h-320 w-60 -translate-y-87.5 rounded-full" />
			</div>
			<div className="space-y-1 px-4 py-8">
				<h1 className="font-mono text-4xl font-bold tracking-wide">
					Blog
				</h1>
				<p className="text-muted-foreground text-base">
					Insights, case studies, and thought leadership from the Nesolearn team.
				</p>
			</div>
			<div className="absolute inset-x-0 h-px w-full border-b border-dashed" />

			{loading ? (
				<div className="p-4 text-muted-foreground text-sm">Loading posts…</div>
			) : posts.length === 0 ? (
				<div className="p-4 text-muted-foreground text-sm">No posts published yet.</div>
			) : (
				<div className="grid p-4 md:grid-cols-2 lg:grid-cols-3 z-10">
					{posts.map((post) => (
						<Link
							to={`/blog/${post.slug}`}
							key={post.id}
							className="group hover:bg-accent/60 active:bg-accent flex flex-col gap-2 rounded-lg p-2 duration-75"
						>
							<LazyImage
								src={post.coverImageUrl}
								fallback={FALLBACK_IMAGE}
								inView={true}
								alt={post.title}
								ratio={16 / 9}
								className="transition-all duration-500 group-hover:scale-105"
							/>
							<div className="space-y-2 px-2 pb-2">
								<div className="text-muted-foreground flex items-center gap-2 text-[11px] sm:text-xs">
									<p>by {post.author?.name || 'Nesolearn Team'}</p>
									<div className="bg-muted-foreground size-1 rounded-full" />
									<p>{formatDate(post.publishedAt)}</p>
									{post.category && (
										<>
											<div className="bg-muted-foreground size-1 rounded-full" />
											<p>{post.category}</p>
										</>
									)}
									{post.contentBody && (
										<>
											<div className="bg-muted-foreground size-1 rounded-full" />
											<p>{estimateReadingTime(post.contentBody)}</p>
										</>
									)}
								</div>
								<h2 className="line-clamp-2 text-lg leading-5 font-semibold tracking-tight">
									{post.title}
								</h2>
								<p className="text-muted-foreground line-clamp-3 text-sm">
									{post.excerpt}
								</p>
							</div>
						</Link>
					))}
				</div>
			)}
		</div>
	);
}

export default BlogSection;